import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T, useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Checkbox, Modal, Tooltip, message } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { find } from 'lodash-es';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder, subscriptionStatus } from 'services/api/pay';
import PackageDlgDetail from './PackageDlgDetail';
import { STRIPE_SUBSCRIPTION_RESULT_TYPE } from './StripeSubscriptionModal';
import { canPayAirwallex } from './Package';
import PayWithSubWindow from 'bizComponents/pay/PayWithSubWindow';
import usePayPackage from '../hooks/usePayPackage';
import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { PackageTimePanel } from './PackageTimePanel';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { refreshBotUsageSummary } from 'hooks/business/system/useBot';
import { refreshComboInfo } from '../hooks/useComboInfo';
import { useQueryClient } from '@tanstack/react-query';
import { org } from 'services/api/admin';
import { useSetOrgValue } from 'hooks/business/system/useOrg';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import wait from 'utils/wait';
import TransComponent from 'components/TransComponent';
import { currencySymbol } from './PriceText';
import { useAutoRenewPayType, useAvailablePayTypes } from 'hooks/business/pay/usePayTypeState';
import { ensureAsaasPaymentWatcher } from '../asaas';
import { isPackageCurrencyLocked, resolvePackageCurrency } from '../packageCurrency';
import { PriceZoneCode, narrowPriceZone, zonePlanName } from '../priceZone';
import { catalogPackageCurrencies, catalogPackageLabel } from '../catalogAdapter';
import { usePriceZones } from '../hooks/usePricing';
import { useSubscriptionChannel } from '../hooks/useSubscriptionChannel';
import ZoneChangeDialog from './ZoneChangeDialog';
import ZoneBar from './ZoneBar';
import PromoteBanner from './PromoteBanner';
import { useMarketPromotionSelection } from '../hooks/useMarketPromotion';
import { logPricing } from '../pricingLogger';

export interface PackageProps {
  orderType: Pay.OrderType;
  orgId: number;
  orderedPackages: Pay.OrderedPackage[];
  packageInfo: Pay.PackageInfoV2;
  /** 当前价区（目录快照或续订更换后的价区） */
  zone: PriceZoneCode;
  /** 可选价区集合（仅续订多于一项时提供更换入口；升级传入单值锁定） */
  zones?: PriceZoneCode[];
  onZoneChange?: (zone: PriceZoneCode) => void;
  /** 升级时锁定币种：原订阅/上次购买币种（后端拒绝升级变更币种） */
  lockedCurrency?: General.Currency;
  /** 已支付货币（price-zones LAST_PURCHASE）：续费并入币种候选（升级的锁定由 lockedCurrency 承担） */
  lastPurchaseCurrency?: General.Currency;
  /** 升级禁选档位（不高于当前档位的目录套餐 ID，续订不传） */
  disabledUpgradeIds?: string[];
  onOrder: OnOrder;
  /** 升级订阅成功后回调（用于关闭弹窗） */
  onSuccess?: () => void;
}

// 升级/续订套餐
function PackageDlg(props: PackageProps) {
  const {
    onOrder,
    onSuccess,
    packageInfo,
    orderedPackages,
    orgId,
    orderType,
    zone,
    zones = [],
    onZoneChange,
    lockedCurrency,
    lastPurchaseCurrency,
    disabledUpgradeIds,
  } = props;

  const { t } = useTranslate();
  const { defaultCurrency } = usePriceZones();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '' } = payload || {};

  const { packageData, recommendData } = packageInfo;
  const { recommendPackageId, recommendDurationId } = recommendData;

  const {
    // 是否企业套餐
    isEnterprise,
    currenciesOptions,
    currency: selectedCurrency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();
  const currency = resolvePackageCurrency({
    selectedCurrency,
    lockedCurrency,
    isEnterprise,
    currenciesOptions,
  });
  const currencyLocked = isPackageCurrencyLocked(lockedCurrency, isEnterprise);
  // 支付方式按生效币种过滤：升级锁币种可能与偏好币种不同，
  // usePayPackage 里的 availablePayTypes 按偏好币种过滤，会错配（锁 BRL 却给支付宝/微信）
  const currencyPayTypes = useAvailablePayTypes(currency);

  // 续订可更换价区（升级锁原价区，zones 只含当前值）
  const [zoneChangeVisible, setZoneChangeVisible] = useState(false);
  const zoneChangeable = orderType === 'RENEW' && zones.length > 1;

  const language = useSystemLanguageValue();
  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum | -1>(0);
  const [canOrder, setCanOrder] = useState(false);
  // 选择的套餐id
  const [checkedPackageId, setCheckedPackageId] = useState<Pay.PackageId | undefined>(
    recommendPackageId,
  );

  const selectedPackage = useMemo(
    () => find(packageInfo.packageData, { id: checkedPackageId }),
    [packageInfo, checkedPackageId],
  );
  // 选中套餐的目录 ID（新版下单传给服务端按目录内联报价）
  const regionPackageId = selectedPackage?.regionPackageId;
  const annualPackage = isEnterprise
    ? undefined
    : selectedPackage?.durationDesc.find((item) => !item.subscription && item.durationNum >= 12);
  useMarketPromotionSelection(orgId, zone, annualPackage);
  // 默认推荐选中不触发 PackageSelectPanel onChange，坐席数需与选中套餐保持同源（否则下单传 0）
  useEffect(() => {
    const pkg = find(packageInfo.packageData, { id: checkedPackageId });
    if (pkg) {
      setAccountNum(pkg.accountNum);
    }
  }, [packageInfo.packageData, checkedPackageId]);
  // 默认选中避开禁选档位（推荐低于当前档位时落到首个可选档，禁选卡不可点不能停留）
  useEffect(() => {
    if (!disabledUpgradeIds?.length) return;
    const checked = find(packageInfo.packageData, { id: checkedPackageId });
    if (checked?.regionPackageId && disabledUpgradeIds.includes(checked.regionPackageId)) {
      const firstEnabled = packageInfo.packageData.find(
        (item) => !item.regionPackageId || !disabledUpgradeIds.includes(item.regionPackageId),
      );
      if (firstEnabled) {
        setCheckedPackageId(firstEnabled.id);
      }
    }
  }, [checkedPackageId, packageInfo.packageData, disabledUpgradeIds]);
  const checkedPackageLabel = useMemo(
    () => catalogPackageLabel(find(packageInfo.packageData, { id: checkedPackageId }), t),
    [packageInfo, checkedPackageId, t],
  );

  useEffect(() => {
    logPricing('package_displayed', {
      orgId,
      orderAction: orderType,
      selectedPriceZone: zone,
      displayedPlanName: zonePlanName(zone, t),
      catalogPriceZone: packageInfo.priceZone,
      catalogRevision: packageInfo.revision,
      selectedPackageId: checkedPackageId,
      selectedRegionPackageId: regionPackageId,
      displayedPackageName: isEnterprise ? t('market.企业版') : checkedPackageLabel,
      isEnterprise,
      currency,
      currencyLocked,
      lockedCurrency,
      selectedAccountNum: find(packageInfo.packageData, { id: checkedPackageId })?.accountNum,
    });
  }, [
    orgId,
    orderType,
    zone,
    packageInfo,
    checkedPackageId,
    regionPackageId,
    isEnterprise,
    checkedPackageLabel,
    currency,
    currencyLocked,
    lockedCurrency,
    t,
  ]);

  // 币种候选（对齐 perseus）：CN 价区固定人民币，其余价区取选中套餐的目录币种集合；
  // 续费并入已支付货币（老套餐可能以任意货币支付过，续费允许沿用；升级只允许已支付
  // 货币，由 lockedCurrency 锁定）。候选需与 PackageDlgDetail 的 currencyCandidates 同源
  const zoneCurrencies = useMemo(() => {
    const base =
      narrowPriceZone(zone) === 'CN'
        ? (['CNY'] as General.Currency[])
        : catalogPackageCurrencies(find(packageInfo.packageData, { id: checkedPackageId }));
    if (
      orderType === 'RENEW' &&
      lastPurchaseCurrency &&
      lastPurchaseCurrency !== 'CNY' &&
      !base.includes(lastPurchaseCurrency)
    ) {
      return [lastPurchaseCurrency, ...base];
    }
    return base;
  }, [zone, packageInfo, checkedPackageId, orderType, lastPurchaseCurrency]);
  // 币种缺省（resolveCatalogCurrency）：锁币种 > 持久化偏好 > 价区 defaultCurrency > 目录首币种
  useEffect(() => {
    if (currencyLocked) return;
    if (!zoneCurrencies.length) return;
    if (selectedCurrency && zoneCurrencies.includes(selectedCurrency)) return;
    const next =
      defaultCurrency && zoneCurrencies.includes(defaultCurrency as General.Currency)
        ? (defaultCurrency as General.Currency)
        : zoneCurrencies[0];
    setCurrency(next);
  }, [zoneCurrencies, selectedCurrency, currencyLocked, defaultCurrency, setCurrency]);
  const [calculateRst, setCalculateRst] = useState<Pay.CalculatePackagePriceRst | undefined>();

  // 当前用户是否为订阅用户（连续包月）
  const isWorkingSubscription = orderedPackages.find((v) => v.status === 1)?.autoRenewing === true;
  const { workingPaymentChannel, subscriptionChannelLoading, subscriptionChannelFailed } =
    useSubscriptionChannel(orgId);
  const { autoRenewPayTypes, selectedAutoRenewPayType, setAutoRenewPayType } =
    useAutoRenewPayType(currency);

  // 是否选择自动续费（连续包月）订阅：
  // 订阅用户升级默认保持订阅；续订默认连续包月
  const [isSubscription, setIsSubscription] = useState(
    orderType === 'UPGRADE' ? isWorkingSubscription : orderType === 'RENEW',
  );
  const [ordering, setOrdering] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  // 升级订阅需要支付验证时的状态
  const [stripePayUrl, setStripePayUrl] = useState('');
  const [stripeUpgradeVisible, setStripeUpgradeVisible] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      // 首次查询是 setTimeout，后续轮询是 setInterval，两种 id 都清理
      clearTimeout(pollingRef.current);
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    // 轮询结束（成功/取消/卸载）时恢复按钮
    setOrdering(false);
  }, []);

  const queryClient = useQueryClient();
  const setOrgValue = useSetOrgValue();

  const successRef = useRef(false);

  const handleUpgradeSuccess = useCallback(() => {
    if (successRef.current) {
      return;
    }
    successRef.current = true;
    message.success(t('扣款成功-邮件-主题', { X: checkedPackageLabel }));
    // 刷新顶部套餐信息与订单数据
    // org 接口有缓存延迟，等 1 秒再拉取（与 Market.refreshAfterPay 一致）
    wait(1000).then(() => {
      org()
        .then(setOrgValue)
        .catch(() => {});
    });
    // Profile 的套餐名来自 orderedPackage、AI 报告等来自 lightWeightPackageInfo
    queryClient.invalidateQueries({ queryKey: ['accounts', 'orderedPackage'] });
    queryClient.invalidateQueries({ queryKey: ['accounts', 'lightWeightPackageInfo'] });
    refreshComboInfo();
    refreshBotUsageSummary();
    onSuccess?.();
  }, [onSuccess, queryClient, setOrgValue, t, checkedPackageLabel]);

  // 轮询订阅状态，直到套餐和帐号数都与目标订阅一致
  const startPolling = useCallback(
    (targetPackageId: Pay.PackageId, targetAccountNum: number) => {
      stopPolling();
      // 轮询期间按钮保持 loading
      setOrdering(true);
      const check = () => {
        // 不传 subscriptionId，查询当前生效订阅
        subscriptionStatus()
          .then((res) => {
            // 尚无有效订阅时继续等待，不将查询空态当成支付成功。
            if (res?.packageId === targetPackageId && res.accountNum === targetAccountNum) {
              stopPolling();
              setStripeUpgradeVisible(false);
              setStripePayUrl('');
              handleUpgradeSuccess();
            }
          })
          .catch(() => {
            // 轮询失败忽略，继续轮询
          });
      };
      // 下单成功后等 1 秒再首次查询（后端状态缓存延迟），之后每 3 秒轮询
      pollingRef.current = setTimeout(() => {
        check();
        pollingRef.current = setInterval(check, 3000);
      }, 1000);
    },
    [handleUpgradeSuccess, stopPolling],
  );

  // 组件卸载时停止轮询
  useEffect(() => stopPolling, [stopPolling]);

  // 监听 Stripe 结果页 postMessage，支付成功/取消立即结束支付验证流程
  // （轮询 subscriptionStatus 作为兜底，防止 postMessage 丢失）
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const { type, result } = e.data || {};
      if (type !== STRIPE_SUBSCRIPTION_RESULT_TYPE) {
        return;
      }
      stopPolling();
      setStripeUpgradeVisible(false);
      setStripePayUrl('');
      if (result === 'success') {
        handleUpgradeSuccess();
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [handleUpgradeSuccess, stopPolling]);

  // 当前生效的套餐
  const curWorkingPackage = useMemo(
    () => orderedPackages.find((v) => v.status === 1),
    [orderedPackages],
  );

  // 实付价格
  const actualPrice = useMemo(() => calculateRst?.payPrice ?? 0, [calculateRst?.payPrice]);

  useEffect(() => {
    if (!isSubscription) {
      setAgreementChecked(false);
    }
  }, [isSubscription]);

  const subscriptionPayType = isWorkingSubscription
    ? workingPaymentChannel
    : selectedAutoRenewPayType;
  const displayedPayTypes = isSubscription
    ? isWorkingSubscription
      ? workingPaymentChannel
        ? [workingPaymentChannel]
        : []
      : autoRenewPayTypes
    : currencyPayTypes;
  // 偏好币种残留的 payType 可能不在生效币种可选列表（偏好 CNY 记住支付宝、升级锁 BRL），归一到首个可选
  const effectivePayType = isSubscription
    ? subscriptionPayType
    : displayedPayTypes.includes(payType)
    ? payType
    : displayedPayTypes[0];
  const subscriptionChangeBlocked =
    workingPaymentChannel === 'asaas' && (orderType !== 'UPGRADE' || !isWorkingSubscription);
  const subscriptionChannelUnavailable =
    isSubscription &&
    (subscriptionChannelLoading ||
      subscriptionChannelFailed ||
      (isWorkingSubscription && !workingPaymentChannel));

  const fetchSubscriptionUpgrade = useCallback(() => {
    const origin =
      import.meta.env.VITE_ENV === 'production'
        ? 'https://mai.zhisuitech.com'
        : 'https://feature-stq-kjds-mai.ftest.leyantech.com';
    if (!effectivePayType) return Promise.resolve();
    const asaas = effectivePayType === 'asaas';
    return onOrder({
      idempotent: `${Date.now()}`,
      accid,
      packageType: isQuickChat ? 3 : 1,
      accountNum,
      durationNum: -1,
      discountTotalPrice: `${actualPrice}`,
      orderAction: orderType,
      payType: effectivePayType,
      currency,
      packageId: checkedPackageId!,
      regionPackageId,
      language,
      bank,
      subscription: true,
      payResultUrl: asaas ? undefined : `${origin}/#/stripe-subscription-result?result=success`,
      cancelResultUrl: asaas ? undefined : `${origin}/#/stripe-subscription-result?result=cancel`,
    })
      .then((res) => {
        const { orderCode, paymentUrl } = res ?? {};
        if (asaas) {
          setOrdering(false);
          const watcher = ensureAsaasPaymentWatcher(orderCode);
          const closeOnSuccess = () => {
            if (watcher.getSnapshot().phase !== 'succeeded') return;
            unsubscribe();
            onSuccess?.();
          };
          const unsubscribe = watcher.subscribe(closeOnSuccess);
          closeOnSuccess();
          return;
        }
        // 有 paymentUrl 时打开支付验证弹窗；
        // 没有（如已绑定支付方式直接扣款）时不打开弹窗
        setStripePayUrl(paymentUrl ?? '');
        setStripeUpgradeVisible(!!paymentUrl);
        if (orderType === 'UPGRADE') {
          // 升级会更换套餐，轮询订阅状态直到 packageId/accountNum 变为目标
          startPolling(checkedPackageId!, accountNum);
        } else if (!paymentUrl) {
          // 续订不改变套餐，无需轮询（轮询条件天然成立会误判）；
          // 未返回支付地址视为已直接扣款成功
          setOrdering(false);
          handleUpgradeSuccess();
        }
      })
      .catch(() => {
        setOrdering(false);
      });
  }, [
    accid,
    accountNum,
    actualPrice,
    bank,
    checkedPackageId,
    currency,
    effectivePayType,
    handleUpgradeSuccess,
    language,
    onOrder,
    onSuccess,
    orderType,
    regionPackageId,
    startPolling,
  ]);

  // 总计价格
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculateRst?.payPrice ?? 0,
      reducePrice: calculateRst?.reducePrice ?? 0,
    };
  }, [calculateRst]);

  useMount(() => {
    const recommendPackage = find(packageData, ['id', recommendPackageId]);
    if (recommendPackage) {
      setAccountNum(recommendPackage.accountNum);
      const recommendDuration = find(recommendPackage.durationDesc, ['id', recommendDurationId]);
      setDurationNum(recommendDuration?.durationNum ?? 0);
    }
  });

  let orderElement = null;
  if (isEnterprise) {
    // 企业版套餐信息
    orderElement = <EnterpriseTotal />;
  } else if (canOrder && !subscriptionChangeBlocked && !subscriptionChannelUnavailable) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          {isSubscription ? (
            <SubscriptionInfo>
              <div>
                {t('自动续费')}：{t('phrase.已开启')}
              </div>
              <div>
                {t('下次扣费')}：{calculateRst?.afterValidEnd}
              </div>
            </SubscriptionInfo>
          ) : (
            calculateRst &&
            curWorkingPackage && (
              <PackageTimePanel
                orderType={orderType}
                curWorkingPackage={curWorkingPackage}
                data={calculateRst}
                currency={currency}
                checkedPackageId={checkedPackageId}
                checkedPackageLabel={checkedPackageLabel}
                // 非订阅分支才会渲染，时长不可能是 -1
                durationNum={durationNum as Pay.DurationNum}
              />
            )
          )}
        </NonEnterpriseTotal>
        {isSubscription && (
          <AgreementLine>
            <Checkbox
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
            >
              <T
                keyName="订购代表接受x"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>
                          }
                        >
                          <AgreementText>{t('自动续费协议')}</AgreementText>
                        </Tooltip>
                      }
                    />
                  ),
                }}
              />
            </Checkbox>
          </AgreementLine>
        )}
        <OrderButton
          type="primary"
          size="large"
          loading={ordering}
          onClick={() => {
            if (effectivePayType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });

              return Promise.resolve();
            }

            if (isSubscription) {
              if (!agreementChecked) {
                setConfirmModalVisible(true);
                return Promise.resolve();
              }
              setOrdering(true);
              // loading 由轮询 startPolling/stopPolling 控制，成功后弹窗关闭
              return fetchSubscriptionUpgrade();
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${totalPrices.actualPrice}`,
              orderAction: orderType,
              payType: effectivePayType!,
              currency,
              packageId: checkedPackageId!,
              regionPackageId,
              language,
              bank,
            });
          }}
        >
          {orderType === 'UPGRADE' ? t('market.立即升级') : t('market.立即续订')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <Content>
        <PromoteBanner className="mb-4" />
        {/* 续订用购买页同款当前方案卡（对齐 perseus new-pricing）；升级是独立形态，不展示 */}
        {orderType === 'RENEW' && (
          <ZoneBar
            zone={zone}
            changeable={zoneChangeable}
            onChange={() => setZoneChangeVisible(true)}
          />
        )}
        <PackageDlgDetail
          orderType={orderType}
          orgId={orgId}
          currency={currency}
          currencyLocked={currencyLocked}
          lastPurchaseCurrency={lastPurchaseCurrency}
          orderedPackages={orderedPackages}
          packageInfo={packageInfo}
          checkedPackageId={checkedPackageId}
          curWorkingPackage={curWorkingPackage}
          disabledUpgradeIds={disabledUpgradeIds}
          setAccountNum={setAccountNum}
          setCanOrder={setCanOrder}
          isEnterprise={isEnterprise}
          setIsEnterprise={setIsEnterprise}
          onPackagePriceChange={setCalculateRst}
          onPackageChange={setCheckedPackageId}
          onDurationChange={setDurationNum}
          setIsSubscription={setIsSubscription}
        />
        {/* 币种切换仅续订提供；升级锁单一币种只读展示（对齐 perseus new-pricing
            不可切换仅展示当前币种），企业版无价格不出币种区 */}
        {isEnterprise ? null : (
          <CurrencyCheckbox
            currencies={orderType === 'RENEW' ? zoneCurrencies : [currency]}
            value={currency}
            onChange={setCurrency}
            currencyDisabled={(value) => currencyLocked && value !== lockedCurrency}
          />
        )}
        {!isEnterprise && displayedPayTypes.length > 0 && effectivePayType ? (
          <PayTypeCheckbox
            payTypes={displayedPayTypes}
            value={effectivePayType}
            onChange={isSubscription && !isWorkingSubscription ? setAutoRenewPayType : setPayType}
            disabled={isSubscription && isWorkingSubscription}
            currency={currency}
            bank={bank}
            onBankChange={setBank}
          />
        ) : null}
        {currency === 'CNY' && (
          <div className="mt-[-12px]">
            <span className="mr-[6px]">
              {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
            </span>
            <CompanyAliHelp />
            <Help />
          </div>
        )}
        {orderElement}
        {subscriptionChangeBlocked && <div>{t('当前Asaas订阅暂不支持续订或取消后的套餐变更')}</div>}
        {!subscriptionChangeBlocked && subscriptionChannelFailed && isSubscription && (
          <div>{t('订阅支付渠道查询失败，请稍后重试')}</div>
        )}
        <Modal
          visible={confirmModalVisible}
          closable={false}
          width={400}
          onOk={() => {
            setConfirmModalVisible(false);
            setAgreementChecked(true);
            setOrdering(true);
            // loading 由轮询 startPolling/stopPolling 控制，成功后弹窗关闭
            fetchSubscriptionUpgrade();
          }}
          onCancel={() => setConfirmModalVisible(false)}
          okText={t('确认订购')}
          cancelText={t('translation.取消')}
        >
          <T
            keyName="确认订购tips"
            params={{
              x: (
                <TransComponent
                  content={
                    <Tooltip
                      title={<div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>}
                    >
                      <AgreementText>{t('自动续费协议')}</AgreementText>
                    </Tooltip>
                  }
                />
              ),
              y: checkedPackageLabel,
              price: `${currencySymbol[currency]} ${actualPrice}`,
            }}
          />
        </Modal>
        {stripeUpgradeVisible && (
          <Modal
            visible={stripeUpgradeVisible}
            title={t(orderType === 'UPGRADE' ? 'market.升级套餐' : 'market.续订套餐')}
            width={480}
            footer={null}
            onCancel={() => {
              stopPolling();
              setStripeUpgradeVisible(false);
              setStripePayUrl('');
            }}
          >
            <UpgradeVerifyContent>
              <SyncOutlined spin style={{ fontSize: 32, color: 'var(--fett-app-primary-color)' }} />
              <PayWithSubWindow
                src={stripePayUrl}
                name="STRIPE_SUBSCRIPTION_UPGRADE_WINDOW"
                expired={false}
                onRefresh={() => Promise.resolve()}
              />
            </UpgradeVerifyContent>
          </Modal>
        )}
        <ZoneChangeDialog
          visible={zoneChangeVisible}
          zones={zones}
          currentZone={zone}
          onCancel={() => setZoneChangeVisible(false)}
          onConfirm={(next) => {
            logPricing('zone_changed', {
              orgId,
              orderAction: orderType,
              previousPriceZone: zone,
              selectedPriceZone: next,
            });
            setZoneChangeVisible(false);
            if (next !== zone) {
              onZoneChange?.(next);
            }
          }}
        />
      </Content>
    </Container>
  );
}

export default PackageDlg;

const Container = styled.div`
  display: flex;
`;

const Content = styled.div`
  flex: 1 0 0;
  /* margin-right: 280px; */
  > * {
    margin-bottom: 20px;
  }
  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: var(--fett-text-primary);
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;

const SubscriptionInfo = styled.div`
  color: var(--fett-text-tertiary);
  > div + div {
    margin-top: 4px;
  }
`;

const AgreementLine = styled.div`
  margin-top: -12px;
`;

const AgreementText = styled.span`
  margin: 0 4px;
  color: var(--fett-app-primary-color);
  cursor: pointer;
`;

const UpgradeVerifyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: var(--fett-text-secondary);
`;
